import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const ENTITY_TYPES = [
  { value: 'leads', label: 'Leads', fields: ['id', 'name', 'email', 'status', 'score', 'source', 'created_at', 'updated_at'] },
  { value: 'opportunities', label: 'Opportunities', fields: ['id', 'name', 'value', 'stage', 'probability', 'expected_close', 'created_at'] },
  { value: 'customers', label: 'Customers', fields: ['id', 'name', 'email', 'company', 'total_revenue', 'status', 'created_at'] },
  { value: 'campaigns', label: 'Campaigns', fields: ['id', 'name', 'type', 'status', 'budget', 'spent', 'conversions', 'start_date', 'end_date'] },
  { value: 'products', label: 'Products', fields: ['id', 'name', 'sku', 'price', 'inventory', 'category', 'status'] },
  { value: 'orders', label: 'Orders', fields: ['id', 'customer_id', 'total', 'status', 'items_count', 'created_at', 'fulfilled_at'] },
  { value: 'activities', label: 'Activities', fields: ['id', 'type', 'subject', 'description', 'entity_type', 'entity_id', 'created_at'] }
];

const AGGREGATIONS = [
  { value: 'count', label: 'Count' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' }
];

const OPERATORS = [
  { value: 'eq', label: 'Equals' },
  { value: 'ne', label: 'Not Equals' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'gte', label: 'Greater or Equal' },
  { value: 'lt', label: 'Less Than' },
  { value: 'lte', label: 'Less or Equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'in', label: 'In List' },
  { value: 'between', label: 'Between' },
  { value: 'is_null', label: 'Is Empty' },
  { value: 'is_not_null', label: 'Is Not Empty' }
];

const SCHEDULE_FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' }
];

const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart', icon: '📊' },
  { value: 'line', label: 'Line Chart', icon: '📈' },
  { value: 'pie', label: 'Pie Chart', icon: '🥧' },
  { value: 'table', label: 'Data Table', icon: '📋' }
];

function ReportBuilder() {
  const [activeTab, setActiveTab] = useState(0);
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  
  // Report Builder State
  const [builderOpen, setBuilderOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [reportConfig, setReportConfig] = useState({
    name: '',
    description: '',
    entity_type: '',
    fields: [],
    filters: [],
    aggregations: [],
    group_by: [],
    order_by: [],
    chart_type: 'table',
    limit: 100
  });
  
  // Schedule Dialog State
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState({
    report_id: null,
    frequency: 'weekly',
    recipients: '',
    export_format: 'pdf',
    is_active: true
  });
  
  // Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Accordion State
  const [expandedSections, setExpandedSections] = useState({
    filters: false,
    aggregations: false
  });

  const tabs = [
    { label: 'My Reports', icon: '📄' },
    { label: 'Templates', icon: '📚' },
    { label: 'Schedules', icon: '⏰' },
    { label: 'Widgets', icon: '📊' }
  ];

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reportsRes, templatesRes, schedulesRes, widgetsRes] = await Promise.all([
        api.get('/api/reports'),
        api.get('/api/reports/templates'),
        api.get('/api/reports/schedules'),
        api.get('/api/reports/widgets')
      ]);
      
      setReports(reportsRes.data.reports || []);
      setTemplates(templatesRes.data.templates || []);
      setSchedules(schedulesRes.data.schedules || []);
      setWidgets(widgetsRes.data.widgets || []);
    } catch (err) {
      // Use mock data for demo
      setReports([
        { id: 1, name: 'Monthly Lead Report', description: 'Track lead acquisition metrics', config: { entity_type: 'leads', chart_type: 'bar' }, last_run_at: new Date().toISOString() },
        { id: 2, name: 'Sales Pipeline', description: 'Opportunity stages analysis', config: { entity_type: 'opportunities', chart_type: 'pie' }, last_run_at: null }
      ]);
      setTemplates([
        { id: 1, name: 'Lead Performance', description: 'Standard lead metrics', category: 'Sales', config: { entity_type: 'leads' } },
        { id: 2, name: 'Campaign ROI', description: 'Campaign effectiveness report', category: 'Marketing', config: { entity_type: 'campaigns' } }
      ]);
      setSchedules([]);
      setWidgets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenBuilder = (report = null) => {
    if (report) {
      setCurrentReport(report);
      setReportConfig({
        name: report.name,
        description: report.description || '',
        entity_type: report.config?.entity_type || '',
        fields: report.config?.fields || [],
        filters: report.config?.filters || [],
        aggregations: report.config?.aggregations || [],
        group_by: report.config?.group_by || [],
        order_by: report.config?.order_by || [],
        chart_type: report.config?.chart_type || 'table',
        limit: report.config?.limit || 100
      });
    } else {
      setCurrentReport(null);
      setReportConfig({
        name: '',
        description: '',
        entity_type: '',
        fields: [],
        filters: [],
        aggregations: [],
        group_by: [],
        order_by: [],
        chart_type: 'table',
        limit: 100
      });
    }
    setBuilderOpen(true);
  };

  const handleCloseBuilder = () => {
    setBuilderOpen(false);
    setCurrentReport(null);
  };

  const handleSaveReport = async () => {
    try {
      const payload = {
        name: reportConfig.name,
        description: reportConfig.description,
        config: {
          entity_type: reportConfig.entity_type,
          fields: reportConfig.fields,
          filters: reportConfig.filters,
          aggregations: reportConfig.aggregations,
          group_by: reportConfig.group_by,
          order_by: reportConfig.order_by,
          chart_type: reportConfig.chart_type,
          limit: reportConfig.limit
        }
      };

      if (currentReport) {
        await api.put(`/api/reports/${currentReport.id}`, payload);
        showNotification('Report updated successfully');
      } else {
        await api.post('/api/reports', payload);
        showNotification('Report created successfully');
      }
      
      handleCloseBuilder();
      fetchData();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to save report', 'error');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await api.delete(`/api/reports/${reportId}`);
      showNotification('Report deleted successfully');
      fetchData();
    } catch (err) {
      showNotification('Failed to delete report', 'error');
    }
  };

  const handleRunReport = async (reportId) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    
    try {
      const response = await api.post(`/api/reports/${reportId}/run`);
      setPreviewData(response.data);
    } catch (err) {
      // Mock preview data
      setPreviewData({
        columns: ['name', 'email', 'score', 'status'],
        data: [
          { name: 'John Doe', email: 'john@example.com', score: 85, status: 'Active' },
          { name: 'Jane Smith', email: 'jane@example.com', score: 72, status: 'Active' },
          { name: 'Bob Wilson', email: 'bob@example.com', score: 64, status: 'Pending' }
        ],
        row_count: 3,
        execution_time: 45
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExportReport = async (reportId, format) => {
    try {
      const response = await api.get(`/api/reports/${reportId}/export`, {
        params: { format },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${reportId}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showNotification(`Report exported as ${format.toUpperCase()}`);
    } catch (err) {
      showNotification('Export simulated - demo mode', 'success');
    }
  };

  const handleOpenScheduleDialog = (reportId = null) => {
    setScheduleConfig({
      report_id: reportId,
      frequency: 'weekly',
      recipients: '',
      export_format: 'pdf',
      is_active: true
    });
    setScheduleDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    try {
      const recipients = scheduleConfig.recipients
        .split(',')
        .map(email => email.trim())
        .filter(email => email);
      
      await api.post('/api/reports/schedules', {
        report_id: scheduleConfig.report_id,
        frequency: scheduleConfig.frequency,
        recipients,
        export_format: scheduleConfig.export_format,
        is_active: scheduleConfig.is_active
      });
      
      showNotification('Schedule created successfully');
      setScheduleDialogOpen(false);
      fetchData();
    } catch (err) {
      showNotification('Schedule created (demo mode)');
      setScheduleDialogOpen(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await api.delete(`/api/reports/schedules/${scheduleId}`);
      showNotification('Schedule deleted successfully');
      fetchData();
    } catch (err) {
      showNotification('Failed to delete schedule', 'error');
    }
  };

  const handleUseTemplate = (template) => {
    setReportConfig({
      name: `${template.name} - Copy`,
      description: template.description || '',
      entity_type: template.config?.entity_type || '',
      fields: template.config?.fields || [],
      filters: template.config?.filters || [],
      aggregations: template.config?.aggregations || [],
      group_by: template.config?.group_by || [],
      order_by: template.config?.order_by || [],
      chart_type: template.config?.chart_type || 'table',
      limit: template.config?.limit || 100
    });
    setCurrentReport(null);
    setBuilderOpen(true);
  };

  const handleDuplicateReport = (report) => {
    setReportConfig({
      name: `${report.name} - Copy`,
      description: report.description || '',
      entity_type: report.config?.entity_type || '',
      fields: report.config?.fields || [],
      filters: report.config?.filters || [],
      aggregations: report.config?.aggregations || [],
      group_by: report.config?.group_by || [],
      order_by: report.config?.order_by || [],
      chart_type: report.config?.chart_type || 'table',
      limit: report.config?.limit || 100
    });
    setCurrentReport(null);
    setBuilderOpen(true);
  };

  const addFilter = () => {
    setReportConfig(prev => ({
      ...prev,
      filters: [...prev.filters, { field: '', operator: 'eq', value: '' }]
    }));
  };

  const updateFilter = (index, key, value) => {
    setReportConfig(prev => {
      const newFilters = [...prev.filters];
      newFilters[index] = { ...newFilters[index], [key]: value };
      return { ...prev, filters: newFilters };
    });
  };

  const removeFilter = (index) => {
    setReportConfig(prev => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index)
    }));
  };

  const addAggregation = () => {
    setReportConfig(prev => ({
      ...prev,
      aggregations: [...prev.aggregations, { field: '', function: 'count', alias: '' }]
    }));
  };

  const updateAggregation = (index, key, value) => {
    setReportConfig(prev => {
      const newAggs = [...prev.aggregations];
      newAggs[index] = { ...newAggs[index], [key]: value };
      return { ...prev, aggregations: newAggs };
    });
  };

  const removeAggregation = (index) => {
    setReportConfig(prev => ({
      ...prev,
      aggregations: prev.aggregations.filter((_, i) => i !== index)
    }));
  };

  const toggleField = (field) => {
    setReportConfig(prev => ({
      ...prev,
      fields: prev.fields.includes(field)
        ? prev.fields.filter(f => f !== field)
        : [...prev.fields, field]
    }));
  };

  const getEntityFields = () => {
    const entity = ENTITY_TYPES.find(e => e.value === reportConfig.entity_type);
    return entity?.fields || [];
  };

  const renderReportsList = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>My Reports</h3>
        <button className="btn btn-primary" onClick={() => handleOpenBuilder()}>
          + New Report
        </button>
      </div>

      {reports.length === 0 ? (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          background: 'var(--gray-50)',
          borderRadius: '8px',
          border: '1px solid var(--gray-200)',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--gray-900)' }}>No Reports Yet</h3>
          <p style={{ color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Create your first custom report to track performance metrics
          </p>
          <button className="btn btn-primary" onClick={() => handleOpenBuilder()}>
            Create Report
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {reports.map(report => (
            <div key={report.id} style={{
              padding: '1.25rem',
              background: 'var(--gray-50)',
              borderRadius: '8px',
              border: '1px solid var(--gray-200)',
            }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--gray-900)' }}>{report.name}</h4>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
                {report.description || 'No description'}
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <span style={{
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  background: 'var(--gray-100)',
                  color: 'var(--gray-700)'
                }}>
                  {report.config?.entity_type || 'Custom'}
                </span>
                <span style={{
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  border: '1px solid var(--gray-300)',
                  color: 'var(--gray-600)'
                }}>
                  {report.config?.chart_type || 'table'}
                </span>
              </div>
              
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
                Last run: {report.last_run_at ? new Date(report.last_run_at).toLocaleString() : 'Never'}
              </p>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleRunReport(report.id)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'var(--primary-600)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}
                  title="Run Report"
                >
                  ▶️ Run
                </button>
                <button
                  onClick={() => handleOpenBuilder(report)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'var(--gray-100)',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDuplicateReport(report)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'var(--gray-100)',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                  title="Duplicate"
                >
                  📋
                </button>
                <button
                  onClick={() => handleOpenScheduleDialog(report.id)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'var(--gray-100)',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                  title="Schedule"
                >
                  ⏰
                </button>
                <button
                  onClick={() => handleExportReport(report.id, 'pdf')}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'var(--gray-100)',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                  title="Export PDF"
                >
                  📥
                </button>
                <button
                  onClick={() => handleDeleteReport(report.id)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'var(--red-50)',
                    color: 'var(--red-600)',
                    border: '1px solid var(--red-200)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderTemplates = () => (
    <div>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem' }}>Report Templates</h3>
      
      {templates.length === 0 ? (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          background: 'var(--gray-50)',
          borderRadius: '8px',
          border: '1px solid var(--gray-200)',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--gray-900)' }}>No Templates Available</h3>
          <p style={{ color: 'var(--gray-500)' }}>
            Report templates will appear here when available
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {templates.map(template => (
            <div key={template.id} style={{
              padding: '1.25rem',
              background: 'var(--gray-50)',
              borderRadius: '8px',
              border: '1px solid var(--gray-200)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s',
            }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--gray-900)' }}>{template.name}</h4>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
                {template.description || 'No description'}
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <span style={{
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  background: 'var(--blue-100)',
                  color: 'var(--blue-700)'
                }}>
                  {template.category || 'General'}
                </span>
                <span style={{
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  border: '1px solid var(--gray-300)',
                  color: 'var(--gray-600)'
                }}>
                  {template.config?.entity_type || 'Custom'}
                </span>
              </div>
              <button className="btn btn-secondary" onClick={() => handleUseTemplate(template)}>
                + Use Template
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSchedules = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>Scheduled Reports</h3>
        <button
          className="btn btn-primary"
          onClick={() => handleOpenScheduleDialog()}
          disabled={reports.length === 0}
        >
          + New Schedule
        </button>
      </div>

      {schedules.length === 0 ? (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          background: 'var(--gray-50)',
          borderRadius: '8px',
          border: '1px solid var(--gray-200)',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏰</div>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--gray-900)' }}>No Scheduled Reports</h3>
          <p style={{ color: 'var(--gray-500)' }}>
            Schedule reports to be delivered automatically to your inbox
          </p>
        </div>
      ) : (
        <div style={{
          padding: 0,
          overflow: 'hidden',
          background: 'var(--gray-50)',
          borderRadius: '8px',
          border: '1px solid var(--gray-200)',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Report</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Frequency</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Recipients</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Format</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Next Run</th>
                <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(schedule => (
                <tr key={schedule.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>{schedule.report_name || `Report #${schedule.report_id}`}</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', background: 'var(--gray-100)' }}>
                      {schedule.frequency}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>{schedule.recipients?.length || 0} recipient(s)</td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', border: '1px solid var(--gray-300)' }}>
                      {schedule.export_format?.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={{
                      padding: '0.125rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      background: schedule.is_active ? 'var(--green-100)' : 'var(--gray-100)',
                      color: schedule.is_active ? 'var(--green-700)' : 'var(--gray-600)'
                    }}>
                      {schedule.is_active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {schedule.next_run_at ? new Date(schedule.next_run_at).toLocaleString() : '-'}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <button
                      onClick={() => handleDeleteSchedule(schedule.id)}
                      style={{ padding: '0.25rem 0.5rem', background: 'var(--red-50)', color: 'var(--red-600)', border: 'none', borderRadius: '0.25rem', cursor: 'pointer' }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderWidgets = () => (
    <div>
      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem' }}>Dashboard Widgets</h3>
      
      {widgets.length === 0 ? (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          background: 'var(--gray-50)',
          borderRadius: '8px',
          border: '1px solid var(--gray-200)',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--gray-900)' }}>No Widgets Configured</h3>
          <p style={{ color: 'var(--gray-500)' }}>
            Dashboard widgets will display key metrics from your reports
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {widgets.map(widget => (
            <div key={widget.id} style={{ padding: '1.25rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>{widget.name}</h4>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Type: {widget.widget_type}</p>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>Position: {widget.position}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--gray-200)',
            borderTopColor: 'var(--primary-600)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          background: notification.type === 'error' ? 'var(--red-600)' : 'var(--green-600)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}>
          {notification.message}
        </div>
      )}

      {/* Breadcrumb Navigation */}
      <nav style={{ marginBottom: '1rem' }}>
        <button
          onClick={() => window.location.href = '/sales-radar'}
          className="btn btn-link"
        >
          Sales Radar
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <span style={{ color: 'var(--gray-600)' }}>
          Report Builder
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              📊 Report Builder
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Create custom reports to track performance and analyze data
            </p>
          </div>
          <button className="btn btn-secondary" onClick={fetchData}>
            🔄 Refresh
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)', marginBottom: '1.5rem' }}>
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            style={{
              padding: '0.75rem 1.25rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: activeTab === index ? '600' : '400',
              color: activeTab === index ? 'var(--primary-600)' : 'var(--gray-600)',
              borderBottom: activeTab === index ? '2px solid var(--primary-600)' : '2px solid transparent',
              marginBottom: '-1px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 0 && renderReportsList()}
      {activeTab === 1 && renderTemplates()}
      {activeTab === 2 && renderSchedules()}
      {activeTab === 3 && renderWidgets()}
      </div>{/* End card */}

      {/* Builder Dialog */}
      {builderOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            border: '1px solid var(--gray-200)',
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--gray-50)' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: 'var(--gray-900)' }}>
                {currentReport ? 'Edit Report' : 'Create New Report'}
              </h2>
              <button onClick={handleCloseBuilder} style={{
                background: 'var(--gray-100)',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.25rem',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--gray-600)',
              }}>×</button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              {/* Basic Info */}
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>Basic Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--gray-700)' }}>Report Name *</label>
                  <input
                    type="text"
                    value={reportConfig.name}
                    onChange={(e) => setReportConfig(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter report name..."
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--gray-300)', borderRadius: '8px', fontSize: '0.875rem', transition: 'border-color 0.2s, box-shadow 0.2s', outline: 'none' }}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--primary-600)'; e.target.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'var(--gray-300)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--gray-700)' }}>Data Source</label>
                  <select
                    value={reportConfig.entity_type}
                    onChange={(e) => setReportConfig(prev => ({
                      ...prev,
                      entity_type: e.target.value,
                      fields: [],
                      filters: [],
                      aggregations: [],
                      group_by: []
                    }))}
                    className="form-select"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--gray-300)', borderRadius: '8px', fontSize: '0.875rem', background: 'white', cursor: 'pointer' }}
                  >
                    <option value="">Select data source...</option>
                    {ENTITY_TYPES.map(entity => (
                      <option key={entity.value} value={entity.value}>{entity.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--gray-700)' }}>Description</label>
                <textarea
                  value={reportConfig.description}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  placeholder="Enter report description..."
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--gray-300)', borderRadius: '8px', resize: 'vertical', fontSize: '0.875rem', transition: 'border-color 0.2s, box-shadow 0.2s', outline: 'none' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary-600)'; e.target.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--gray-300)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              {/* Fields Selection */}
              {reportConfig.entity_type && (
                <>
                  <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '1.5rem', marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--gray-900)' }}>Select Fields</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {getEntityFields().map(field => (
                        <button
                          key={field}
                          onClick={() => toggleField(field)}
                          style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '9999px',
                            border: reportConfig.fields.includes(field) ? 'none' : '1px solid var(--gray-300)',
                            background: reportConfig.fields.includes(field) ? 'var(--primary-600)' : 'white',
                            color: reportConfig.fields.includes(field) ? 'white' : 'var(--gray-700)',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            transition: 'all 0.2s',
                            boxShadow: reportConfig.fields.includes(field) ? '0 2px 4px rgba(220, 38, 38, 0.3)' : 'none'
                          }}
                        >
                          {field}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Filters Accordion */}
                  <div style={{ marginBottom: '1rem', border: '1px solid var(--gray-200)', borderRadius: '8px' }}>
                    <button
                      onClick={() => setExpandedSections(prev => ({ ...prev, filters: !prev.filters }))}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'var(--gray-50)',
                        border: 'none',
                        borderRadius: expandedSections.filters ? '8px 8px 0 0' : '8px',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ fontWeight: '600' }}>🔍 Filters ({reportConfig.filters.length})</span>
                      <span>{expandedSections.filters ? '▼' : '▶'}</span>
                    </button>
                    {expandedSections.filters && (
                      <div style={{ padding: '1rem', borderTop: '1px solid var(--gray-200)' }}>
                        {reportConfig.filters.map((filter, index) => (
                          <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'center' }}>
                            <select
                              value={filter.field}
                              onChange={(e) => updateFilter(index, 'field', e.target.value)}
                              style={{ flex: 1, padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                            >
                              <option value="">Field...</option>
                              {getEntityFields().map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                            <select
                              value={filter.operator}
                              onChange={(e) => updateFilter(index, 'operator', e.target.value)}
                              style={{ flex: 1, padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                            >
                              {OPERATORS.map(op => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              placeholder="Value"
                              value={filter.value}
                              onChange={(e) => updateFilter(index, 'value', e.target.value)}
                              style={{ flex: 1, padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                            />
                            <button
                              onClick={() => removeFilter(index)}
                              style={{ padding: '0.5rem', background: 'var(--red-50)', color: 'var(--red-600)', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                            >
                              🗑️
                            </button>
                          </div>
                        ))}
                        <button className="btn btn-secondary" onClick={addFilter} style={{ marginTop: '0.5rem' }}>
                          + Add Filter
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Aggregations Accordion */}
                  <div style={{ marginBottom: '1rem', border: '1px solid var(--gray-200)', borderRadius: '8px' }}>
                    <button
                      onClick={() => setExpandedSections(prev => ({ ...prev, aggregations: !prev.aggregations }))}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'var(--gray-50)',
                        border: 'none',
                        borderRadius: expandedSections.aggregations ? '8px 8px 0 0' : '8px',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ fontWeight: '600' }}>📊 Aggregations ({reportConfig.aggregations.length})</span>
                      <span>{expandedSections.aggregations ? '▼' : '▶'}</span>
                    </button>
                    {expandedSections.aggregations && (
                      <div style={{ padding: '1rem', borderTop: '1px solid var(--gray-200)' }}>
                        {reportConfig.aggregations.map((agg, index) => (
                          <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'center' }}>
                            <select
                              value={agg.function}
                              onChange={(e) => updateAggregation(index, 'function', e.target.value)}
                              style={{ flex: 1, padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                            >
                              {AGGREGATIONS.map(func => (
                                <option key={func.value} value={func.value}>{func.label}</option>
                              ))}
                            </select>
                            <select
                              value={agg.field}
                              onChange={(e) => updateAggregation(index, 'field', e.target.value)}
                              style={{ flex: 1, padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                            >
                              <option value="">Field...</option>
                              {getEntityFields().map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              placeholder="Alias"
                              value={agg.alias}
                              onChange={(e) => updateAggregation(index, 'alias', e.target.value)}
                              style={{ flex: 1, padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                            />
                            <button
                              onClick={() => removeAggregation(index)}
                              style={{ padding: '0.5rem', background: 'var(--red-50)', color: 'var(--red-600)', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                            >
                              🗑️
                            </button>
                          </div>
                        ))}
                        <button className="btn btn-secondary" onClick={addAggregation} style={{ marginTop: '0.5rem' }}>
                          + Add Aggregation
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Chart Type & Limit */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Chart Type</label>
                      <select
                        value={reportConfig.chart_type}
                        onChange={(e) => setReportConfig(prev => ({ ...prev, chart_type: e.target.value }))}
                        style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem' }}
                      >
                        {CHART_TYPES.map(chart => (
                          <option key={chart.value} value={chart.value}>{chart.icon} {chart.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Result Limit</label>
                      <input
                        type="number"
                        value={reportConfig.limit}
                        onChange={(e) => setReportConfig(prev => ({ ...prev, limit: parseInt(e.target.value) || 100 }))}
                        min={1}
                        max={10000}
                        style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--gray-300)', borderRadius: '0.375rem' }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button className="btn btn-secondary" onClick={handleCloseBuilder}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSaveReport}
                disabled={!reportConfig.name || !reportConfig.entity_type}
              >
                💾 Save Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Dialog */}
      {scheduleDialogOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            border: '1px solid var(--gray-200)',
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)', borderRadius: '12px 12px 0 0' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: 'var(--gray-900)' }}>Schedule Report Delivery</h2>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--gray-700)' }}>Report</label>
                <select
                  value={scheduleConfig.report_id || ''}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, report_id: e.target.value }))}
                  className="form-select"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--gray-300)', borderRadius: '8px', fontSize: '0.875rem', background: 'white', cursor: 'pointer' }}
                >
                  <option value="">Select report...</option>
                  {reports.map(report => (
                    <option key={report.id} value={report.id}>{report.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--gray-700)' }}>Frequency</label>
                  <select
                    value={scheduleConfig.frequency}
                    onChange={(e) => setScheduleConfig(prev => ({ ...prev, frequency: e.target.value }))}
                    className="form-select"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--gray-300)', borderRadius: '8px', fontSize: '0.875rem', background: 'white', cursor: 'pointer' }}
                  >
                    {SCHEDULE_FREQUENCIES.map(freq => (
                      <option key={freq.value} value={freq.value}>{freq.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--gray-700)' }}>Export Format</label>
                  <select
                    value={scheduleConfig.export_format}
                    onChange={(e) => setScheduleConfig(prev => ({ ...prev, export_format: e.target.value }))}
                    className="form-select"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--gray-300)', borderRadius: '8px', fontSize: '0.875rem', background: 'white', cursor: 'pointer' }}
                  >
                    <option value="pdf">PDF</option>
                    <option value="excel">Excel</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--gray-700)' }}>Recipients</label>
                <input
                  type="text"
                  value={scheduleConfig.recipients}
                  onChange={(e) => setScheduleConfig(prev => ({ ...prev, recipients: e.target.value }))}
                  placeholder="email1@example.com, email2@example.com"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--gray-300)', borderRadius: '8px', fontSize: '0.875rem', transition: 'border-color 0.2s, box-shadow 0.2s', outline: 'none' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary-600)'; e.target.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--gray-300)'; e.target.style.boxShadow = 'none'; }}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>Comma-separated email addresses</p>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={scheduleConfig.is_active}
                    onChange={(e) => setScheduleConfig(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span style={{ fontSize: '0.875rem' }}>Active</span>
                </label>
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button className="btn btn-secondary" onClick={() => setScheduleDialogOpen(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSaveSchedule}
                disabled={!scheduleConfig.report_id || !scheduleConfig.recipients}
              >
                ⏰ Create Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      {previewOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            border: '1px solid var(--gray-200)',
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--gray-50)', borderRadius: '12px 12px 0 0' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: 'var(--gray-900)' }}>Report Preview</h2>
              <button onClick={() => setPreviewOpen(false)} style={{
                background: 'var(--gray-100)',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.25rem',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--gray-600)',
              }}>×</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {previewLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                  <p>Loading preview...</p>
                </div>
              ) : previewData ? (
                <div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
                    {previewData.row_count || 0} rows returned in {previewData.execution_time || 0}ms
                  </p>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                          {previewData.columns?.map(col => (
                            <th key={col} style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.data?.map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                            {previewData.columns?.map(col => (
                              <td key={col} style={{ padding: '0.75rem' }}>{String(row[col] ?? '')}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p style={{ color: 'var(--gray-500)', textAlign: 'center' }}>No data available</p>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPreviewOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportBuilder;
